import React, { FunctionComponent } from 'react';
import styled from 'styled-components';
import { ContainerProps, StyledContainerProps } from './ContainerTypes';
import {
  MOBILE_COLS,
  DESKTOP_COLS,
  DASHBOARD_COLS,
  getColsNum,
} from './helpers';
import { screens } from '../styles/breakpoints';

const StyledContainer = styled.div<StyledContainerProps>`
  /** flex fallback for older browsers */
  display: flex;
  flex-flow: wrap;
  /** Mobile grid */
  width: 100%;
  ${({ full }) =>
    (typeof full === 'boolean' && full) || (full && full.xs)
      ? ''
      : `
        padding-left: 4px;
        padding-right: 4px;
      `}
  margin-left: auto;
  margin-right: auto;
  /** Tablet grid */
  ${({ theme }) => theme.breakpoints.up('sm')} {
    ${({ full }) =>
      (typeof full === 'boolean' && full) || (full && full.sm)
        ? ''
        : `
          padding-left: 12px;
          padding-right: 12px;
        `}
  }
  /** Desktop grid */
  ${({ theme }) => theme.breakpoints.up('md')} {
    padding-left: 0;
    padding-right: 0;
    max-width: ${({ full }) =>
      (typeof full === 'boolean' && full) || (full && full.md)
        ? '100%'
        : '1240px'};
  }
`;

const StyledGridContainer = styled(StyledContainer)<StyledContainerProps>`
  @supports (display: grid) {
    /** Mobile grid */
    display: ${({ cols }) =>
      getColsNum('xs', cols) > 0 ? '-ms-grid' : 'none'};
    display: ${({ cols }) => (getColsNum('xs', cols) > 0 ? 'grid' : 'none')};
    -ms-grid-columns: repeat(${({ cols }) => getColsNum('xs', cols)}, 1fr);
    grid-template-columns: repeat(${({ cols }) => getColsNum('xs', cols)}, 1fr);
    /** Tablet and Desktop grid */
    ${({ theme, cols }) => {
      let responsiveStyles = '';

      Object.keys(screens).forEach((k, i) => {
        const key = k as 'sm' | 'md' | 'lg';
        const colsNum = getColsNum(key, cols);

        if (
          colsNum >= 0 &&
          (i === 0
            ? colsNum !== getColsNum('xs', cols)
            : colsNum !==
              getColsNum(
                Object.keys(screens)[i - 1] as 'sm' | 'md' | 'lg',
                cols,
              ))
        ) {
          responsiveStyles = responsiveStyles.concat(`
            ${theme.breakpoints.up(key)} {
              display: ${colsNum > 0 ? '-ms-grid' : 'none'};
              display: ${colsNum > 0 ? 'grid' : 'none'};
              -ms-grid-columns: repeat(
                ${colsNum},
                1fr
              );
              grid-template-columns: repeat(
                ${colsNum},
                1fr
              );
            }
          `);
        }
      });

      return responsiveStyles;
    }}
  }
`;

const Container: FunctionComponent<ContainerProps> = ({
  children,
  as = 'div',
  onlyFlex,
  className,
  type = 'default',
  cols: colsProp,
  full: fullProp,
  ...other
}) => {
  const cols =
    type === 'dashboard'
      ? DASHBOARD_COLS
      : {
          xs: MOBILE_COLS,
          sm: DESKTOP_COLS,
        };
  const full = type === 'dashboard' ? true : fullProp;

  const props = {
    as,
    className,
    cols: colsProp || cols,
    full,
    ...other,
  };

  return onlyFlex ? (
    <StyledContainer {...props}>{children}</StyledContainer>
  ) : (
    <StyledGridContainer {...props}>{children}</StyledGridContainer>
  );
};

export default Container;
